import React, { useState } from 'react';
import styled from 'styled-components';
import { deleteUser } from '../services/apiService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 400px;
  color: ${props => props.theme.colors.text};
`;

const ModalTitle = styled.h2`
  margin: 0 0 30px 0;
  text-align: center;
  color: #d32f2f;
  font-size: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 4px;
  font-size: 14px;
  background-color: ${props => props.theme.colors.inputBackground || props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary || '#007bff'};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 4px;
  font-size: 14px;
  background-color: ${props => props.theme.colors.inputBackground || props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary || '#007bff'};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ConfirmationText = styled.p`
  font-size: 12px;
  color: #888;
  margin-top: 8px;
  margin-bottom: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 30px;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #d32f2f;
  color: white;

  &:hover:not(:disabled) {
    background-color: #b71c1c;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${props => props.theme.colors.borderColor};
  color: ${props => props.theme.colors.text};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.borderColor};
    opacity: 0.8;
  }
`;

const ErrorMessage = styled.div`
  background-color: #6a1b1b;
  border: 1px solid #8b2e2e;
  color: #ff8b8b;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const DeleteUserConfirmModal = ({ isOpen, onClose, users, onUserDeleted }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [confirmationInput, setConfirmationInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedUser = users.find(u => u.id === selectedUserId);

  const handleDelete = async () => {
    setError('');

    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    if (confirmationInput !== selectedUser.username) {
      setError(`Please type "${selectedUser.username}" to confirm deletion`);
      return;
    }

    setLoading(true);
    try {
      await deleteUser(selectedUser.userId);
      setSelectedUserId('');
      setConfirmationInput('');
      onUserDeleted();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = selectedUserId && selectedUser && confirmationInput === selectedUser.username;

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Delete User</ModalTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <FormGroup>
          <Label htmlFor="userSelect">Select User to Delete</Label>
          <Select
            id="userSelect"
            value={selectedUserId}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setConfirmationInput('');
              setError('');
            }}
            disabled={loading}
          >
            <option value="">-- Choose a user --</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username}
              </option>
            ))}
          </Select>
        </FormGroup>

        {selectedUser && (
          <FormGroup>
            <Label htmlFor="confirmationInput">
              Type "<strong>{selectedUser.username}</strong>" to confirm
            </Label>
            <Input
              id="confirmationInput"
              type="text"
              value={confirmationInput}
              onChange={(e) => {
                setConfirmationInput(e.target.value);
                setError('');
              }}
              placeholder={selectedUser.username}
              disabled={loading}
            />
            <ConfirmationText>
              This action cannot be undone.
            </ConfirmationText>
          </FormGroup>
        )}

        <ButtonGroup>
          <DeleteButton
            onClick={handleDelete}
            disabled={loading || !isFormValid}
          >
            {loading ? '...' : 'Delete'}
          </DeleteButton>
          <CancelButton
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </CancelButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default DeleteUserConfirmModal;
